import React from 'react';
import { useTranslation } from 'react-i18next';
import DragDropFileUpload from './DragDropFileUpload';

interface FileUploadSectionProps {
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  firstTwoLines: { [key: string]: string };
  selectedFiles?: File[];
  multiple?: boolean;
  onRemoveFile?: (index: number) => void;
  onClearAll?: () => void;
  dropLabelKeys?: { drop: string; dropOr: string; namespace: string };
  hideDropZoneWhenFileSelected?: boolean;
  showFilePreview?: boolean;
  accept?: string;
  disabled?: boolean;
  disabledHint?: string;
  hideSelectedFilesHeader?: boolean;
}

const FileUploadSection: React.FC<FileUploadSectionProps> = ({
  onFileChange,
  fileInputRef,
  firstTwoLines,
  selectedFiles = [],
  multiple = true,
  onRemoveFile,
  onClearAll,
  dropLabelKeys,
  hideDropZoneWhenFileSelected = false,
  showFilePreview = true,
  accept = ".csv,.txt",
  disabled = false,
  disabledHint,
  hideSelectedFilesHeader = false,
}) => {
  const { t } = useTranslation('dataUpload');

  return (
    <>
      {/* Drag and Drop File Upload */}
      <div className="mb-3 sm:mb-4">
        <h2 className="text-sm sm:text-base md:text-lg font-semibold mb-2 sm:mb-3">
          {t('fileInput.label')}
        </h2>
        <DragDropFileUpload
          onFileChange={onFileChange}
          fileInputRef={fileInputRef}
          accept={accept}
          multiple={multiple}
          selectedFiles={selectedFiles}
          onRemoveFile={onRemoveFile}
          onClearAll={onClearAll}
          dropLabelKeys={dropLabelKeys}
          showDropZone={!(hideDropZoneWhenFileSelected && selectedFiles.length > 0)}
          disabled={disabled}
          hideSelectedFilesHeader={hideSelectedFilesHeader}
        />
        {disabled && disabledHint && (
          <p className="mt-1 text-sm text-muted-foreground" role="note">{disabledHint}</p>
        )}
      </div>

      {/* File Preview */}
      {showFilePreview && Object.keys(firstTwoLines).length > 0 && (
        <div className="file-preview-container">
          {Object.entries(firstTwoLines).map(([filename, lines]) => (
            <div key={filename} className="file-preview-item">
              <div className="file-preview-filename text-xs sm:text-sm">{filename}</div>
              <div className="file-preview-content text-[10px] sm:text-xs md:text-sm">
                {lines.split(/([,;])/).map((part, i) => (
                  <span key={i} className={part === ',' || part === ';' ? 'delimiter-highlight' : ''}>
                    {part}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default FileUploadSection;